import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { companyCarsApi } from '../services/companyCarsApi';
import { useToast } from '../components/ui/Toast';
import type {
    CompanyCar,
    CarBrand,
    CarFuelType,
    CarLeasingCompany,
    CarInsuranceCompany,
    CarEmployee,
    CompanyOption,
    CreateCarData,
    UpdateCarData,
    CarSortField,
    SortDir,
} from '../types/companyCars';

export function useCompanyCars() {
    const { toast } = useToast();

    // Data state
    const [cars, setCars] = useState<CompanyCar[]>([]);
    const [brands, setBrands] = useState<CarBrand[]>([]);
    const [fuelTypes, setFuelTypes] = useState<CarFuelType[]>([]);
    const [leasingCompanies, setLeasingCompanies] = useState<CarLeasingCompany[]>([]);
    const [insuranceCompanies, setInsuranceCompanies] = useState<CarInsuranceCompany[]>([]);
    const [employees, setEmployees] = useState<CarEmployee[]>([]);
    const [companies, setCompanies] = useState<CompanyOption[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Filters
    const [companyFilter, setCompanyFilter] = useState('');
    const [activeOnly, setActiveOnly] = useState(true);
    const [searchText, setSearchText] = useState('');

    // Sort
    const [sortField, setSortField] = useState<CarSortField>('DriverName');
    const [sortDir, setSortDir] = useState<SortDir>('asc');

    const handleSort = useCallback((field: CarSortField) => {
        setSortDir(prev => sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc');
        setSortField(field);
    }, [sortField]);

    const fetchCars = useCallback(async () => {
        try {
            const data = await companyCarsApi.listCars(companyFilter || undefined, activeOnly);
            setCars(data);
        } catch (err: any) {
            toast(err.message || 'Failed to load cars', 'error');
        }
    }, [companyFilter, activeOnly, toast]);

    const fetchLookups = useCallback(async () => {
        const [brandsRes, fuelRes, leasingRes, insuranceRes, empRes, compRes] = await Promise.allSettled([
            companyCarsApi.listBrands(),
            companyCarsApi.listFuelTypes(),
            companyCarsApi.listLeasingCompanies(),
            companyCarsApi.listInsuranceCompanies(),
            companyCarsApi.getEmployees(),
            companyCarsApi.getCompanies(),
        ]);
        if (brandsRes.status === 'fulfilled') setBrands(brandsRes.value);
        if (fuelRes.status === 'fulfilled') setFuelTypes(fuelRes.value);
        if (leasingRes.status === 'fulfilled') setLeasingCompanies(leasingRes.value);
        if (insuranceRes.status === 'fulfilled') setInsuranceCompanies(insuranceRes.value);
        if (empRes.status === 'fulfilled') setEmployees(empRes.value);
        if (compRes.status === 'fulfilled') setCompanies(compRes.value);
    }, []);

    // Initial load: fetch everything, show spinner
    useEffect(() => {
        let mounted = true;
        (async () => {
            setIsLoading(true);
            await Promise.allSettled([
                companyCarsApi.listCars(undefined, true).then(d => mounted && setCars(d)),
                fetchLookups(),
            ]);
            if (mounted) setIsLoading(false);
        })();
        return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Filter changes: re-fetch cars only, NO spinner (keeps table visible)
    const isInitialMount = useRef(true);
    useEffect(() => {
        if (isInitialMount.current) { isInitialMount.current = false; return; }
        fetchCars();
    }, [fetchCars]);

    // Client-side search + sort
    const filteredSortedCars = useMemo(() => {
        let result = cars;

        // Search filter
        if (searchText.trim()) {
            const term = searchText.toLowerCase();
            result = result.filter(c =>
                c.DriverName?.toLowerCase().includes(term) ||
                c.BrandName?.toLowerCase().includes(term) ||
                c.LicensePlate?.toLowerCase().includes(term) ||
                c.CarModel?.toLowerCase().includes(term)
            );
        }

        // Sort
        result = [...result].sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];
            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return 1;
            if (bVal == null) return -1;
            const cmp = typeof aVal === 'string'
                ? aVal.localeCompare(bVal as string, undefined, { sensitivity: 'base' })
                : (aVal as number) - (bVal as number);
            return sortDir === 'asc' ? cmp : -cmp;
        });

        return result;
    }, [cars, searchText, sortField, sortDir]);

    // CRUD operations
    // NOTE: create/update re-throw after toasting so the edit modal stays open on error
    // (its handleSubmit only calls onClose() when onSave resolves). This is the fix for
    // the silent-save-failure bug.
    const createCar = useCallback(async (data: CreateCarData) => {
        try {
            await companyCarsApi.createCar(data);
            toast('Car added successfully', 'success');
            await fetchCars();
        } catch (err: any) {
            toast(err.message || 'Failed to add car', 'error');
            throw err;
        }
    }, [fetchCars, toast]);

    const updateCar = useCallback(async (id: number, data: UpdateCarData) => {
        try {
            await companyCarsApi.updateCar(id, data);
            toast('Car updated successfully', 'success');
            await fetchCars();
        } catch (err: any) {
            toast(err.message || 'Failed to update car', 'error');
            throw err;
        }
    }, [fetchCars, toast]);

    const deleteCar = useCallback(async (id: number) => {
        try {
            await companyCarsApi.deleteCar(id);
            toast('Car deleted successfully', 'success');
            await fetchCars();
        } catch (err: any) {
            toast(err.message || 'Failed to delete car', 'error');
        }
    }, [fetchCars, toast]);

    // Export
    const exportCars = useCallback(async () => {
        try {
            const blob = await companyCarsApi.exportCars(companyFilter || undefined, activeOnly);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const d = new Date();
            const localDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            a.download = `Company_Cars_${companyFilter || 'All'}_${localDate}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
            toast('Export downloaded', 'success');
        } catch (err: any) {
            toast(err.message || 'Export failed', 'error');
        }
    }, [companyFilter, activeOnly, toast]);

    // Lookup CRUD
    const createBrand = useCallback(async (brandName: string) => {
        const result = await companyCarsApi.createBrand(brandName);
        toast('Brand added', 'success');
        const data = await companyCarsApi.listBrands();
        setBrands(data);
        return result.id;
    }, [toast]);

    const updateBrand = useCallback(async (id: number, data: { brandName?: string; sortOrder?: number; isActive?: boolean }) => {
        await companyCarsApi.updateBrand(id, data);
        toast('Brand updated', 'success');
        setBrands(await companyCarsApi.listBrands());
    }, [toast]);

    const createFuelType = useCallback(async (data: { fuelCode: string; fuelLabel: string; isElectric?: boolean }) => {
        await companyCarsApi.createFuelType(data);
        toast('Fuel type added', 'success');
        setFuelTypes(await companyCarsApi.listFuelTypes());
    }, [toast]);

    const updateFuelType = useCallback(async (id: number, data: { fuelCode?: string; fuelLabel?: string; isElectric?: boolean; sortOrder?: number; isActive?: boolean }) => {
        await companyCarsApi.updateFuelType(id, data);
        toast('Fuel type updated', 'success');
        setFuelTypes(await companyCarsApi.listFuelTypes());
    }, [toast]);

    const createLeasingCompany = useCallback(async (companyName: string) => {
        const result = await companyCarsApi.createLeasingCompany(companyName);
        toast('Leasing company added', 'success');
        setLeasingCompanies(await companyCarsApi.listLeasingCompanies());
        return result.id;
    }, [toast]);

    const updateLeasingCompany = useCallback(async (id: number, data: { companyName?: string; sortOrder?: number; isActive?: boolean }) => {
        await companyCarsApi.updateLeasingCompany(id, data);
        toast('Leasing company updated', 'success');
        setLeasingCompanies(await companyCarsApi.listLeasingCompanies());
    }, [toast]);

    const createInsuranceCompany = useCallback(async (companyName: string) => {
        const result = await companyCarsApi.createInsuranceCompany(companyName);
        toast('Insurance company added', 'success');
        setInsuranceCompanies(await companyCarsApi.listInsuranceCompanies());
        return result.id;
    }, [toast]);

    const updateInsuranceCompany = useCallback(async (id: number, data: { companyName?: string; sortOrder?: number; isActive?: boolean }) => {
        await companyCarsApi.updateInsuranceCompany(id, data);
        toast('Insurance company updated', 'success');
        setInsuranceCompanies(await companyCarsApi.listInsuranceCompanies());
    }, [toast]);

    return {
        cars: filteredSortedCars,
        totalCars: cars.length,
        brands, fuelTypes, leasingCompanies, insuranceCompanies, employees, companies, isLoading,
        companyFilter, setCompanyFilter, activeOnly, setActiveOnly,
        searchText, setSearchText,
        sortField, sortDir, handleSort,
        createCar, updateCar, deleteCar, exportCars, refreshCars: fetchCars,
        createBrand, updateBrand, createFuelType, updateFuelType,
        createLeasingCompany, updateLeasingCompany,
        createInsuranceCompany, updateInsuranceCompany,
    };
}
